import {
  Activity,
  ChevronRight,
  Clock,
  ExternalLink,
  Headphones,
  Pause,
  Play,
  Radio,
  RefreshCw,
  Signal,
  WifiOff,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Empty } from '../components/ui/empty';
import { Spinner } from '../components/ui/spinner';
import { useChannels, useNowPlaying } from '../lib/api/channels';
import { useDaySchedule } from '../lib/api/schedule';
import { useLocaleStore } from '../lib/locales';
import { useRadioPlayerStore } from '../lib/radioPlayerStore';
import type { NowPlayingResponse, PublicChannel, ScheduleEntry } from '../lib/types';
import { cn } from '../lib/utils';

const REFRESH_MS = 10_000;

// Lagos = UTC+1, no DST
function lagosNow(): Date {
  return new Date(Date.now() + 60 * 60 * 1000);
}

function lagosDateStr(): string {
  return lagosNow().toISOString().slice(0, 10);
}

export default function NowPlayingMonitor(): JSX.Element {
  const channels = useChannels();
  const locale = useLocaleStore((s) => s.current);
  const items = channels.data?.items ?? [];

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const single = items.length === 1 ? items[0] : null;

  return (
    <>
      <Topbar title="Now playing" subtitle="Live status · auto-refreshes every 10 s" />
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Radio className="h-5 w-5 text-brand" /> Now playing
          </span>
        }
        subtitle="Schedule and stream stats. Refreshes every 10 seconds."
      />

      <div className="px-6 pb-12">
        {channels.isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner label="Loading channels…" size="lg" />
          </div>
        ) : items.length === 0 ? (
          <Empty title="No channels" description="Add a channel first via the Channels page." />
        ) : single ? (
          <SingleChannelView channel={single} locale={locale} />
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {items.map((ch) => (
              <ChannelCard key={ch.slug} channel={ch} locale={locale} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ── Single-channel hero ───────────────────────────────────────────────────────

function SingleChannelView({ channel, locale }: { channel: PublicChannel; locale: string }): JSX.Element {
  const np = useNowPlaying(channel.slug, locale);
  const radio = useRadioPlayerStore();
  const isPlaying = radio.playing && radio.channelSlug === channel.slug;
  const todayDate = lagosDateStr();
  const schedule = useDaySchedule(channel.slug, locale, todayDate);

  const handleRadio = () => {
    if (!channel.streamUrl) return;
    // Pass the raw upstream — the store wraps with the current baseUrl when
    // setting audio.src. See radioPlayerStore.ts persist version=2.
    radio.setChannel(channel.slug, channel.name, channel.streamUrl);
  };

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {/* Left: main content */}
      <div className="flex flex-col gap-5 lg:col-span-2">

        {/* Channel identity bar */}
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-line bg-surface px-6 py-4 shadow-card">
          <div className="flex items-center gap-4">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand/10 ring-1 ring-brand/20">
              <Radio className="h-5 w-5 text-brand" />
              {channel.isActive ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-success ring-2 ring-surface" />
                </span>
              ) : null}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-[17px] font-semibold leading-tight tracking-tight text-ink">
                  {channel.name}
                </h2>
                <Badge variant={channel.isActive ? 'success' : 'neutral'}>
                  {channel.isActive ? 'Live' : 'Inactive'}
                </Badge>
              </div>
              {channel.description ? (
                <p className="mt-0.5 text-[13px] text-ink-muted">{channel.description}</p>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {np.isFetching ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin text-ink-subtle" />
            ) : null}
            {channel.streamUrl ? (
              <button
                type="button"
                onClick={handleRadio}
                title={isPlaying ? 'Pause stream' : 'Play stream in sidebar'}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all',
                  isPlaying
                    ? 'border-brand bg-brand text-white shadow-[0_0_0_3px_theme(colors.brand/0.15)]'
                    : 'border-line bg-surface text-ink-muted hover:border-brand/40 hover:bg-brand/5 hover:text-brand',
                )}
              >
                {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3 translate-x-px" />}
                {isPlaying ? 'Pause' : 'Play'}
              </button>
            ) : null}
            {channel.streamUrl ? (
              <a
                href={channel.streamUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-muted hover:border-line-strong hover:text-ink"
              >
                <ExternalLink className="h-3 w-3" /> Stream
              </a>
            ) : null}
          </div>
        </div>

        {/* Offline alert */}
        {np.data?.stream !== null && np.data?.stream?.online === false ? (
          <div className="flex items-center gap-3 rounded-xl border border-danger/25 bg-danger/5 px-5 py-3.5">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-danger" />
            </span>
            <p className="text-sm font-medium text-danger">
              Stream is offline — the broadcast signal has dropped.
            </p>
          </div>
        ) : null}

        {/* Stream stats + on-air */}
        {np.isLoading ? (
          <div className="flex items-center justify-center rounded-2xl border border-line bg-surface py-12">
            <Spinner label="Loading…" />
          </div>
        ) : np.data ? (
          <>
            <StreamHero data={np.data} channel={channel} />
            <OnAirHero data={np.data} />
          </>
        ) : (
          <div className="rounded-2xl border border-line bg-surface px-6 py-5 text-sm text-ink-muted">
            Could not load now-playing data.
          </div>
        )}

        {/* Today's full schedule */}
        {schedule.data?.entries && schedule.data.entries.length > 0 ? (
          <TodaySchedule entries={schedule.data.entries} currentSlotId={np.data?.current?.slotId ?? null} />
        ) : null}
      </div>

      {/* Right: sidebar */}
      <div className="flex flex-col gap-5">
        {np.data ? <UpNextPanel data={np.data} /> : null}
        {np.data ? (
          <div className="flex items-center justify-end gap-1.5 text-[11px] text-ink-subtle">
            <Activity className="h-3 w-3" />
            <span>
              Updated at{' '}
              {new Date(np.data.asOf).toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}{' '}
              (Lagos)
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Stream hero ───────────────────────────────────────────────────────────────

function StreamHero({ data, channel }: { data: NowPlayingResponse; channel: PublicChannel }): JSX.Element {
  const stream = data.stream;

  if (!channel.streamStatsUrl && !stream) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-line bg-surface px-5 py-3.5 text-sm text-ink-muted">
        <WifiOff className="h-4 w-4 shrink-0" /> No stream stats URL configured.
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-line bg-surface px-5 py-3.5 text-sm text-ink-muted">
        <WifiOff className="h-4 w-4 shrink-0 text-danger" /> Stream stats unavailable.
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-6 rounded-2xl border px-6 py-5',
        stream.online ? 'border-success/25 bg-success/5' : 'border-line bg-surface',
      )}
    >
      {/* Status */}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            stream.online ? 'bg-success/10' : 'bg-surface-muted',
          )}
        >
          {stream.online ? (
            <span className="relative flex h-3.5 w-3.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-50" />
              <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-success" />
            </span>
          ) : (
            <WifiOff className="h-4 w-4 text-ink-subtle" />
          )}
        </div>
        <div>
          <p className={cn('text-sm font-semibold', stream.online ? 'text-success' : 'text-ink-muted')}>
            {stream.online ? 'On air' : 'Offline'}
          </p>
          {stream.title ? (
            <p className="text-xs text-ink-muted">{stream.title}</p>
          ) : (
            <p className="text-xs text-ink-subtle">
              {stream.online ? 'Broadcasting live' : 'Signal lost'}
            </p>
          )}
        </div>
      </div>

      {/* Stats */}
      {(stream.listeners !== null || stream.bitrate !== null) ? (
        <div className="flex items-center gap-8">
          {stream.listeners !== null ? (
            <div className="flex flex-col items-end">
              <p className="text-[22px] font-semibold tabular-nums leading-none text-ink">
                {stream.listeners.toLocaleString()}
              </p>
              <p className="mt-1 flex items-center gap-1 text-[11px] text-ink-muted">
                <Headphones className="h-3 w-3" /> listeners
              </p>
            </div>
          ) : null}
          {stream.bitrate !== null ? (
            <div className="flex flex-col items-end">
              <p className="text-[22px] font-semibold tabular-nums leading-none text-ink">
                {stream.bitrate}
              </p>
              <p className="mt-1 flex items-center gap-1 text-[11px] text-ink-muted">
                <Signal className="h-3 w-3" /> kbps
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── On-air hero ───────────────────────────────────────────────────────────────

function OnAirHero({ data }: { data: NowPlayingResponse }): JSX.Element {
  const entry = data.current;

  return (
    <div className="overflow-hidden rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/8 via-brand/4 to-transparent">
      {/* Header strip */}
      <div className="flex items-center gap-2 border-b border-brand/10 bg-brand/5 px-6 py-3">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
        </span>
        <p className="text-[11px] font-bold uppercase tracking-widest text-brand/80">On air now</p>
      </div>

      <div className="px-6 py-5">
        {entry ? (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <Link
                  to={`/programmes/${encodeURIComponent(entry.programmeSlug)}`}
                  className="block text-[20px] font-semibold leading-snug tracking-tight text-brand hover:underline"
                >
                  {entry.title}
                </Link>
                {entry.summary ? (
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{entry.summary}</p>
                ) : null}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-subtle">
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {entry.startTime.slice(0, 5)} – {entry.endTime.slice(0, 5)}
                  </span>
                  <span>{entry.durationMinutes} min</span>
                  {entry.wrapsMidnight ? <Badge variant="outline">Overnight</Badge> : null}
                </div>
              </div>
              <Link
                to={`/programmes/${encodeURIComponent(entry.programmeSlug)}`}
                className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand/60 transition-colors hover:bg-brand/20 hover:text-brand"
              >
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <SlotProgress entry={entry} />
          </>
        ) : (
          <p className="text-sm text-ink-muted">Nothing scheduled right now.</p>
        )}
      </div>
    </div>
  );
}

// ── Slot progress bar ─────────────────────────────────────────────────────────

function SlotProgress({ entry }: { entry: ScheduleEntry }): JSX.Element {
  const [pct, setPct] = useState(() => calcProgress(entry));
  useEffect(() => {
    const id = setInterval(() => setPct(calcProgress(entry)), 10_000);
    return () => clearInterval(id);
  }, [entry]);

  if (pct === null) return <></>;

  return (
    <div className="mt-4">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-brand/15">
        <div
          className="h-full rounded-full bg-brand transition-all duration-1000"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between">
        <p className="text-[10.5px] text-brand/50">Progress</p>
        <p className="text-[10.5px] font-medium text-brand/70">{Math.round(pct)}%</p>
      </div>
    </div>
  );
}

function calcProgress(entry: ScheduleEntry): number | null {
  const now = lagosNow();
  const todayPrefix = now.toISOString().slice(0, 10);
  const start = new Date(`${todayPrefix}T${entry.startTime}`);
  const end = new Date(`${todayPrefix}T${entry.endTime}`);
  if (end <= start) end.setDate(end.getDate() + 1);
  const total = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  if (total <= 0) return null;
  return Math.min(100, Math.max(0, (elapsed / total) * 100));
}

function minutesUntil(timeStr: string): number {
  const now = lagosNow();
  const todayPrefix = now.toISOString().slice(0, 10);
  const target = new Date(`${todayPrefix}T${timeStr}`);
  if (target.getTime() < now.getTime()) target.setDate(target.getDate() + 1);
  return Math.round((target.getTime() - now.getTime()) / 60_000);
}

// ── Up next panel ─────────────────────────────────────────────────────────────

function UpNextPanel({ data }: { data: NowPlayingResponse }): JSX.Element {
  const entry = data.next;
  const [minsAway, setMinsAway] = useState(() => (entry ? minutesUntil(entry.startTime) : null));

  useEffect(() => {
    if (!entry) return;
    const id = setInterval(() => setMinsAway(minutesUntil(entry.startTime)), 30_000);
    return () => clearInterval(id);
  }, [entry]);

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
      <div className="flex items-center justify-between border-b border-line-hair px-5 py-3.5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">Up next</p>
        {minsAway !== null ? (
          <span className="rounded-full bg-brand/8 px-2.5 py-0.5 text-[11px] font-semibold text-brand">
            in {minsAway} min
          </span>
        ) : null}
      </div>

      <div className="px-5 py-4">
        {entry ? (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Link
                to={`/programmes/${encodeURIComponent(entry.programmeSlug)}`}
                className="block truncate text-[14px] font-semibold text-ink hover:text-brand hover:underline"
              >
                {entry.title}
              </Link>
              {entry.summary ? (
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-ink-muted">{entry.summary}</p>
              ) : null}
              <p className="mt-2 flex items-center gap-1.5 text-[11.5px] text-ink-subtle">
                <Clock className="h-3 w-3" />
                {entry.startTime.slice(0, 5)} – {entry.endTime.slice(0, 5)}
                <span className="text-line">·</span>
                {entry.durationMinutes} min
              </p>
            </div>
            <Link
              to={`/programmes/${encodeURIComponent(entry.programmeSlug)}`}
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-ink-subtle transition-colors hover:bg-surface-muted hover:text-ink"
            >
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <p className="text-sm text-ink-muted">Nothing scheduled after this.</p>
        )}
      </div>
    </div>
  );
}

// ── Today's full schedule ─────────────────────────────────────────────────────

function TodaySchedule({
  entries,
  currentSlotId,
}: {
  entries: ScheduleEntry[];
  currentSlotId: string | null;
}): JSX.Element {
  const sorted = [...entries].sort((a, b) => a.startTime.localeCompare(b.startTime));

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
      <div className="border-b border-line-hair bg-surface-muted/40 px-6 py-3.5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
          Today's schedule
        </p>
      </div>
      <ul className="divide-y divide-line-hair">
        {sorted.map((entry) => {
          const isCurrent = entry.slotId === currentSlotId;
          return (
            <li
              key={entry.slotId}
              className={cn(
                'group flex items-center gap-4 px-6 py-3 transition-colors hover:bg-surface-muted/50',
                isCurrent && 'bg-brand/4',
              )}
            >
              {/* Time */}
              <div className="w-[52px] shrink-0 text-right">
                <p className={cn('text-xs font-semibold tabular-nums', isCurrent ? 'text-brand' : 'text-ink-muted')}>
                  {entry.startTime.slice(0, 5)}
                </p>
                <p className="text-[10px] tabular-nums text-ink-subtle/60">
                  {entry.durationMinutes}m
                </p>
              </div>

              {/* Dot */}
              <div className="flex w-5 shrink-0 justify-center">
                {isCurrent ? (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-60" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand" />
                  </span>
                ) : (
                  <span className="h-2 w-2 rounded-full border-2 border-line bg-surface" />
                )}
              </div>

              {/* Programme */}
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Link
                  to={`/programmes/${encodeURIComponent(entry.programmeSlug)}`}
                  className={cn(
                    'truncate text-[13px] font-medium hover:underline',
                    isCurrent ? 'text-brand' : 'text-ink hover:text-brand',
                  )}
                >
                  {entry.title}
                </Link>
                {isCurrent ? (
                  <Badge variant="brand" className="shrink-0 text-[10px]">Live</Badge>
                ) : null}
                {entry.wrapsMidnight ? (
                  <Badge variant="outline" className="shrink-0 text-[10px]">Overnight</Badge>
                ) : null}
              </div>

              {/* Edit link */}
              <Link
                to={`/schedule?slot=${entry.slotId}`}
                title="Edit in schedule"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-ink-subtle opacity-0 transition-all hover:bg-surface-muted hover:text-ink group-hover:opacity-100"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Multi-channel card ────────────────────────────────────────────────────────

function ChannelCard({ channel, locale }: { channel: PublicChannel; locale: string }): JSX.Element {
  const np = useNowPlaying(channel.slug, locale);
  const radio = useRadioPlayerStore();
  const isPlaying = radio.playing && radio.channelSlug === channel.slug;

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand/10 ring-1 ring-brand/20">
              <Radio className="h-4 w-4 text-brand" />
              {channel.isActive ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success ring-2 ring-surface" />
                </span>
              ) : null}
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="truncate">{channel.name}</CardTitle>
              {channel.description ? (
                <p className="mt-0.5 truncate text-[12px] text-ink-muted">{channel.description}</p>
              ) : null}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {np.isFetching ? <RefreshCw className="h-3 w-3 animate-spin text-ink-subtle" /> : null}
            <Badge variant={channel.isActive ? 'success' : 'neutral'}>
              {channel.isActive ? 'Live' : 'Off'}
            </Badge>
            {channel.streamUrl ? (
              <button
                type="button"
                onClick={() =>
                  radio.setChannel(channel.slug, channel.name, channel.streamUrl!)
                }
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full transition-all duration-150',
                  isPlaying
                    ? 'bg-brand text-white shadow-[0_0_0_3px_theme(colors.brand/0.18)]'
                    : 'bg-brand/10 text-brand hover:bg-brand hover:text-white',
                )}
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3 translate-x-px" />}
              </button>
            ) : null}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-3 pt-3">
        {np.isLoading ? (
          <div className="flex flex-1 items-center justify-center py-4">
            <Spinner label="Loading…" size="sm" />
          </div>
        ) : np.data ? (
          <CardBody data={np.data} channel={channel} />
        ) : (
          <p className="text-sm text-ink-muted">Could not load data.</p>
        )}
      </CardContent>
    </Card>
  );
}

function CardBody({ data, channel }: { data: NowPlayingResponse; channel: PublicChannel }): JSX.Element {
  const stream = data.stream;

  return (
    <div className="flex flex-1 flex-col gap-3">
      {/* Stream status */}
      {(channel.streamStatsUrl || stream) ? (
        <div
          className={cn(
            'flex items-center justify-between gap-2 rounded-lg border px-3 py-2',
            stream?.online ? 'border-success/25 bg-success/5' : 'border-line bg-surface-muted/50',
          )}
        >
          <div className="flex items-center gap-2">
            {stream?.online ? (
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-50" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
              </span>
            ) : (
              <WifiOff className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
            )}
            <span className={cn('text-xs font-medium', stream?.online ? 'text-success' : 'text-ink-muted')}>
              {stream ? (stream.online ? 'Streaming' : 'Offline') : 'Stats unavailable'}
            </span>
          </div>
          {stream?.listeners !== null && stream?.listeners !== undefined ? (
            <span className="flex items-center gap-1 text-[11px] font-medium text-ink-muted">
              <Headphones className="h-3 w-3" />
              {stream.listeners.toLocaleString()}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* On air */}
      <div className="flex-1 overflow-hidden rounded-lg border border-brand/15 bg-gradient-to-br from-brand/7 to-brand/3 p-3">
        <div className="mb-2 flex items-center gap-1.5">
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
          </span>
          <p className="text-[10px] font-bold uppercase tracking-widest text-brand/70">On air</p>
        </div>

        {data.current ? (
          <>
            <Link
              to={`/programmes/${encodeURIComponent(data.current.programmeSlug)}`}
              className="block truncate text-[13.5px] font-semibold text-brand hover:underline"
            >
              {data.current.title}
            </Link>
            <p className="mt-1 flex items-center gap-1 text-[11px] text-ink-subtle">
              <Clock className="h-3 w-3" />
              {data.current.startTime.slice(0, 5)} – {data.current.endTime.slice(0, 5)}
            </p>
            <SlotProgress entry={data.current} />
          </>
        ) : (
          <p className="text-xs text-ink-muted">Nothing scheduled.</p>
        )}
      </div>

      {/* Up next */}
      {data.next ? (
        <div className="rounded-lg border border-line bg-surface px-3 py-2.5">
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-widest text-ink-subtle">Up next</p>
            <span className="rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-semibold text-ink-subtle">
              in {minutesUntil(data.next.startTime)} min
            </span>
          </div>
          <Link
            to={`/programmes/${encodeURIComponent(data.next.programmeSlug)}`}
            className="block truncate text-[13px] font-medium text-ink hover:text-brand hover:underline"
          >
            {data.next.title}
          </Link>
          <p className="mt-0.5 flex items-center gap-1 text-[11px] text-ink-subtle">
            <Clock className="h-3 w-3" />
            {data.next.startTime.slice(0, 5)} – {data.next.endTime.slice(0, 5)}
          </p>
        </div>
      ) : null}

      <p className="mt-auto flex items-center justify-end gap-1 text-[10.5px] text-ink-subtle">
        <Activity className="h-3 w-3" />
        {new Date(data.asOf).toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </p>
    </div>
  );
}
